/**
 * SIMPLE KNOWLEDGE SERVICE
 * Busca simples em knowledge_documents sem embeddings
 */

import { supabase } from '../lib/supabase';

export interface KnowledgeDocument {
  id: string;
  assistant_id: string;
  title: string;
  content: string;
  content_type: string;
  size: number;
  created_by: string;
  metadata: any;
  created_at: string;
  updated_at: string;
}

export interface SimpleSearchResult {
  id: string;
  title: string;
  content: string;
  relevance: number; // 0-1, baseado em match simples
}

class SimpleKnowledgeService {
  /**
   * Busca documentos relevantes usando busca textual simples
   */
  async searchDocuments(
    assistantId: string,
    query: string,
    limit: number = 3
  ): Promise<SimpleSearchResult[]> {
    try {
      if (!query || query.trim().length === 0) {
        return [];
      }

      // Buscar documentos do assistente
      const { data, error } = await supabase
        .from('knowledge_documents')
        .select('*')
        .eq('assistant_id', assistantId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Erro ao buscar documentos:', error);
        return [];
      }

      if (!data || data.length === 0) {
        return [];
      }

      // Ranquear documentos por relevância (busca simples por palavras-chave)
      const queryWords = query.toLowerCase().split(/\s+/).filter(w => w.length > 2);
      
      const rankedDocs = data.map((doc) => {
        const titleLower = doc.title.toLowerCase();
        const contentLower = doc.content.toLowerCase();
        
        // Calcular relevância baseado em matches de palavras-chave
        let score = 0;
        
        queryWords.forEach((word) => {
          // Match no título vale mais
          if (titleLower.includes(word)) {
            score += 0.3;
          }
          
          // Match no conteúdo
          const contentMatches = (contentLower.match(new RegExp(word, 'g')) || []).length;
          score += contentMatches * 0.1;
        });
        
        return {
          id: doc.id,
          title: doc.title,
          content: doc.content,
          relevance: Math.min(score, 1) // Normalizar para 0-1
        };
      });

      // Ordenar por relevância e retornar top N
      return rankedDocs
        .filter(doc => doc.relevance > 0) // Apenas docs com alguma relevância
        .sort((a, b) => b.relevance - a.relevance)
        .slice(0, limit);

    } catch (error) {
      console.error('❌ Erro na busca de conhecimento:', error);
      return [];
    }
  }

  /**
   * Listar todos os documentos de um assistente
   */
  async listDocuments(assistantId: string): Promise<KnowledgeDocument[]> {
    try {
      const { data, error } = await supabase
        .from('knowledge_documents')
        .select('*')
        .eq('assistant_id', assistantId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('❌ Erro ao listar documentos:', error);
      return [];
    }
  }

  /**
   * Adicionar um documento
   */
  async addDocument(
    assistantId: string,
    title: string,
    content: string,
    userId: string,
    contentType: string = 'text',
    metadata: any = {}
  ): Promise<KnowledgeDocument | null> {
    try {
      const { data, error } = await supabase
        .from('knowledge_documents')
        .insert({
          assistant_id: assistantId,
          title,
          content,
          content_type: contentType,
          size: content.length,
          created_by: userId,
          metadata
        })
        .select()
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('❌ Erro ao adicionar documento:', error);
      throw error;
    }
  }

  /**
   * Deletar um documento
   */
  async deleteDocument(documentId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('knowledge_documents')
        .delete()
        .eq('id', documentId);

      if (error) throw error;
    } catch (error) {
      console.error('❌ Erro ao deletar documento:', error);
      throw error;
    }
  }

  /**
   * Obter estatísticas da base de conhecimento
   */
  async getStats(assistantId: string): Promise<{
    totalDocuments: number;
    totalSize: number;
  }> {
    try {
      const { data, error } = await supabase
        .from('knowledge_documents')
        .select('size')
        .eq('assistant_id', assistantId);

      if (error) throw error;

      const totalDocuments = data?.length || 0;
      const totalSize = data?.reduce((sum, doc) => sum + (doc.size || 0), 0) || 0;

      return { totalDocuments, totalSize };
    } catch (error) {
      console.error('❌ Erro ao obter estatísticas:', error);
      return { totalDocuments: 0, totalSize: 0 };
    }
  }
}

export const simpleKnowledgeService = new SimpleKnowledgeService();

